import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from '@/domains/internal/auth/services/authService';
import { logger } from '@/utils/logger';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  requiredPermissions?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles, requiredPermissions }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await authService.getCurrentUser();
        const authenticated = !!user && authService.isAuthenticated();
        setIsAuthenticated(authenticated);

        if (authenticated) {
          // Check if this is a referral user trying to access staff routes
          const isReferralPortalRoute = location.pathname.startsWith('/referral-portal');
          const isReferralUser = user.user_type === 'referral' || user.user_type === 'referral_team';

          // Redirect referral users away from staff routes
          if (isReferralUser && !isReferralPortalRoute) {
            window.location.href = '/referral-portal/appointments';
            return;
          }

          // Redirect staff users away from referral portal routes
          if (!isReferralUser && isReferralPortalRoute) {
            window.location.href = '/dashboard';
            return;
          }

          if (requiredRoles || requiredPermissions) {
            let hasRoleAccess = true;
            let hasPermissionAccess = true;

            if (requiredRoles) {
              hasRoleAccess = authService.hasAnyRole(requiredRoles);
            }

            if (requiredPermissions) {
              hasPermissionAccess = authService.hasAnyPermission(requiredPermissions);
            }

            setHasPermission(hasRoleAccess && hasPermissionAccess);
          } else {
            setHasPermission(true);
          }
        }
      } catch (error) {
        logger.error('Authentication check failed:', error);
        setIsAuthenticated(false);
        setHasPermission(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [requiredRoles, requiredPermissions, location.pathname]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to appropriate login page based on the route
    const isReferralPortalRoute = location.pathname.startsWith('/referral-portal');
    const loginPath = isReferralPortalRoute ? '/referral-portal/login' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  if ((requiredRoles || requiredPermissions) && !hasPermission) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page.
          </p>
          {requiredRoles && (
            <p className="text-sm text-gray-500">
              Required roles: {requiredRoles.join(', ')}
            </p>
          )}
          {requiredPermissions && (
            <p className="text-sm text-gray-500">
              Required permissions: {requiredPermissions.join(', ')}
            </p>
          )}
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;